import React, { useState } from 'react';
import { Alert, Button, DatePicker, Input, Modal, Select, Space, Typography, message } from 'antd';
import { ShareAltOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import InviteManager from './InviteManager';
import { taskAccess } from '../../../services/task_access';

export default function TabInvitation({ taskId }: { taskId: string }) {
  const [shareOpen, setShareOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [expiry, setExpiry] = useState('7d');
  const [customExpiry, setCustomExpiry] = useState<dayjs.Dayjs | null>(null);
  const [link, setLink] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [inviteVersion, setInviteVersion] = useState(0);
  const [linkName, setLinkName] = useState('');
  const [inviteLinks, setInviteLinks] = useState<Record<string, string>>({});

  const openShare = () => {
    setLinkName('');
    setLink(''); setExpiresAt(''); setExpiry('7d'); setCustomExpiry(null); setShareOpen(true);
  };
  const createLink = async () => {
    if (creating || link) return;
    setCreating(true);
    try {
      const result = await taskAccess('invite', {
        task_id: taskId, expiry,
        ...(linkName.trim() && { name: linkName.trim() }),
        ...(expiry === 'custom' && { expires_at: customExpiry?.toISOString() }),
      });
      const url = new URL(window.location.href);
      url.pathname = `/task/detail/${encodeURIComponent(taskId)}`;
      url.search = '';
      url.hash = new URLSearchParams({ invite: result.token }).toString();
      setLink(url.toString());
      if (result.invite_id) setInviteLinks(previous => ({ ...previous, [result.invite_id]: url.toString() }));
      setExpiresAt(dayjs(result.expires_at).format('DD/MM/YYYY HH:mm'));
      setInviteVersion(value => value + 1);
    } catch (e: any) { message.error(e.message); }
    finally { setCreating(false); }
  };
  const revokeLinks = async () => {
    try {
      await taskAccess('revoke-invites', { task_id: taskId });
      setLink(''); setShareOpen(false);
      setInviteVersion(value => value + 1);
      message.success('ยกเลิกลิงก์เชิญทั้งหมดแล้ว');
    } catch (e: any) { message.error(e.message); }
  };
  return <div>
    <Space wrap style={{ width: '100%', justifyContent: 'flex-end', marginBottom: 16 }}>
      <Button onClick={revokeLinks}>ยกเลิกลิงก์เชิญทั้งหมด</Button>
      <Button type="primary" icon={<ShareAltOutlined />} onClick={openShare}>Share</Button>
    </Space>
    <InviteManager taskId={taskId} version={inviteVersion} links={inviteLinks} />
    <Modal title="Share task" open={shareOpen} onCancel={() => setShareOpen(false)} closable={!creating} maskClosable={!creating} keyboard={!creating} destroyOnHidden footer={[
      <Button key="cancel" onClick={() => setShareOpen(false)} disabled={creating}>Cancel</Button>,
      <Button key="create" type="primary" onClick={createLink} loading={creating} disabled={!!link || (expiry === 'custom' && (!customExpiry || !customExpiry.isAfter(dayjs())))}>Create</Button>,
    ]}>
      <p>สร้างลิงก์เพื่อให้ผู้อื่นเปิด task และขอสิทธิ์ ผู้รับยังต้องรอเจ้าของอนุมัติ และกด Register ก่อนสร้างทีมและส่งงาน</p>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <label htmlFor="share-name">ชื่อลิงก์ (ไม่บังคับ)</label>
        <Input id="share-name" value={linkName} onChange={event => setLinkName(event.target.value)} maxLength={120} disabled={creating || !!link} placeholder="เว้นว่างเพื่อตั้งชื่อ Link 01, Link 02, … อัตโนมัติ" />
        <label htmlFor="share-expiry">อายุลิงก์เชิญ (TTL นับจากเวลาสร้าง)</label>
        <Select id="share-expiry" value={expiry} onChange={setExpiry} disabled={creating || !!link} style={{ width: '100%' }} options={[
          { value: '1h', label: '1 ชั่วโมง' }, { value: '1d', label: '1 วัน' },
          { value: '7d', label: '7 วัน' }, { value: '30d', label: '30 วัน' }, { value: 'custom', label: 'กำหนดเอง' },
        ]} />
        {expiry === 'custom' && <DatePicker aria-label="วันและเวลาหมดอายุ" showTime format="DD/MM/YYYY HH:mm" value={customExpiry} onChange={setCustomExpiry} disabled={creating || !!link} disabledDate={value => value.isBefore(dayjs(), 'day')} style={{ width: '100%' }} placeholder="เลือกวันและเวลาหมดอายุ" />}
        {link && <>
          <Alert type="success" message={`สร้างลิงก์แล้ว หมดอายุ ${expiresAt} (เวลาของคุณ)`} />
          <Input aria-label="ลิงก์เข้า task" value={link} readOnly onFocus={event => event.target.select()} suffix={<span style={{ borderInlineStart: '1px solid #d9d9d9', paddingInlineStart: 8, marginInlineStart: 4, display: 'inline-flex', alignItems: 'center' }}><Typography.Text copyable={{ text: link }} /></span>} />
        </>}
      </Space>
    </Modal>
  </div>;
}
